import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { authAPI } from '../../services/api';
import awtfLogo from '../../assets/awtf_logo.png';

export default function Register() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [invitation, setInvitation] = useState(null);
  const [pageError, setPageError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loadingInvitation, setLoadingInvitation] = useState(true);
  const navigate = useNavigate();
  const { register, loading, error, clearError } = useAuthStore();

  useEffect(() => {
    const loadInvitation = async () => {
      if (!token) {
        setPageError('Invitation link is required to create an account.');
        setLoadingInvitation(false);
        return;
      }

      try {
        const response = await authAPI.getInvitation(token);
        setInvitation(response);
      } catch (loadError) {
        setPageError(loadError.response?.data?.error || 'Invitation link is invalid or expired.');
      } finally {
        setLoadingInvitation(false);
      }
    };

    loadInvitation();
  }, [token]);

  const needsEmail = !!invitation && !invitation.email;

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearError();
    setPageError('');

    if (needsEmail && !email.trim()) {
      setPageError('Please enter your email address.');
      return;
    }

    if (password !== confirmPassword) {
      setPageError('Passwords do not match.');
      return;
    }

    const success = await register(token, password, needsEmail ? email.trim() : undefined);
    if (success) {
      navigate('/login');
    }
  };

  const inputCls = 'w-full rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-400/20';
  const btnCls = 'w-full rounded-2xl bg-[linear-gradient(135deg,#f07415,#1b2857)] py-3 text-sm font-semibold text-white shadow-[0_12px_25px_rgba(240,116,21,0.40)] transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0';

  return (
    <div className="min-h-screen bg-[linear-gradient(145deg,#1b2857_0%,#4a1a06_45%,#f07415_100%)] flex items-center justify-center px-4">

      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute left-[-6rem] top-[-6rem] h-[22rem] w-[22rem] rounded-full bg-[#f07415]/20 blur-3xl" />
        <div className="absolute right-[-6rem] bottom-[-6rem] h-[22rem] w-[22rem] rounded-full bg-[#1b2857]/40 blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">

        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-[28px] bg-white/95 p-3 shadow-[0_16px_40px_rgba(0,0,0,0.3)] ring-1 ring-white/30 backdrop-blur">
            <img src={awtfLogo} alt="AWTF Logo" className="h-full w-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-white">AWTF Portal</h1>
          <p className="mt-1 text-sm text-orange-100/80">Advocates Welfare Trust Fund</p>
          <p className="mt-2 text-xs font-medium uppercase tracking-[0.22em] text-orange-100/60">
            Set up your account
          </p>
        </div>

        <div className="overflow-hidden rounded-[32px] border border-white/12 bg-white shadow-[0_40px_80px_rgba(0,0,0,0.4)] p-6">

          {(pageError || error) && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {pageError || error}
            </div>
          )}

          {loadingInvitation && (
            <p className="py-6 text-center text-sm text-slate-500">Loading invitation…</p>
          )}

          {!loadingInvitation && invitation && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-slate-600">
                Welcome, <span className="font-semibold text-slate-900">{invitation.member?.full_name}</span>.
                Create a password to activate your account.
              </p>

              {needsEmail && (
                <div>
                  <label className="mb-2 block text-sm font-semibold text-slate-700">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    className={inputCls}
                    placeholder="you@example.com"
                    required
                  />
                  <p className="mt-1.5 text-xs text-slate-400">
                    You'll use this email (with your password) to log in going forward.
                  </p>
                </div>
              )}

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className={inputCls}
                  placeholder="Create password"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  className={inputCls}
                  placeholder="Confirm password"
                  required
                />
              </div>

              <button type="submit" disabled={loading} className={btnCls}>
                {loading ? 'Creating Account…' : 'Create Account'}
              </button>
            </form>
          )}

          {!loadingInvitation && !invitation && pageError && (
            <p className="text-center text-sm text-slate-500">
              Please contact AWTF for a new invitation link.
            </p>
          )}

          <p className="mt-6 text-center text-sm text-slate-500">
            Already joined?{' '}
            <Link to="/login" className="font-semibold text-orange-600 hover:underline">
              Login here
            </Link>
          </p>
        </div>

        <p className="mt-6 text-center text-xs text-orange-100/50">
          &copy; {new Date().getFullYear()} Advocates Welfare Trust Fund
        </p>
      </div>
    </div>
  );
}
